import React, { useState, useEffect } from 'react';
import { Modal, Row, Col } from 'react-bootstrap';
import { Button } from '../layout/buttons';
import { createStakeholder } from '../../utils/stakeholders';
import { fetchUserCompanies } from '../../utils/companies';
import { logger } from '../../utils/logger';
import styles from './Canvasses.module.css';

const StakeholderCreationButton = ({ onSuccess, parentCompanyId, selectedStakeholderId }) => {
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [createdStakeholder, setCreatedStakeholder] = useState(null);
  const [userCompanies, setUserCompanies] = useState([]);
  const [companiesLoading, setCompaniesLoading] = useState(false);
  const [stakeholderData, setStakeholderData] = useState({
    name: '',
    parentCompanyId: parentCompanyId || '',
    isInternal: true,
    isPublic: false,
    objective: '',
    metadata: {
      stakeholderType: '',
      department: '',
      region: ''
    }
  });

  useEffect(() => {
    loadUserCompanies();
  }, []);

  useEffect(() => {
    // Reset created stakeholder if a different stakeholder is selected
    if (createdStakeholder && selectedStakeholderId !== createdStakeholder.id) {
      setCreatedStakeholder(null);
    }
  }, [selectedStakeholderId, createdStakeholder]);

  const loadUserCompanies = async () => {
    try {
      setCompaniesLoading(true);
      const result = await fetchUserCompanies({ pageSize: 50 });
      setUserCompanies(result.companies || []);
      
      // Always use parentCompanyId if provided, otherwise select first company
      if (result.companies && result.companies.length > 0) {
        if (parentCompanyId) {
          // Use provided parentCompanyId as default selection
          setStakeholderData(prev => ({
            ...prev,
            parentCompanyId: parentCompanyId
          }));
        } else {
          // Auto-select first company if no parentCompanyId provided
          setStakeholderData(prev => ({
            ...prev,
            parentCompanyId: result.companies[0].id
          }));
        }
      }
    } catch (err) {
      logger.error('Error loading user organizations:', err);
    } finally {
      setCompaniesLoading(false);
    }
  };

  const handleCreateStakeholder = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError('');

      if (!stakeholderData.name.trim()) {
        setError('Stakeholder name is required');
        return;
      }

      // Validate that a company is selected
      if (!stakeholderData.parentCompanyId) {
        setError('Please select an organization for the stakeholder');
        return;
      }

      const result = await createStakeholder(stakeholderData);
      
      if (result.success) {
        setShowModal(false);
        setStakeholderData({
          name: '',
          parentCompanyId: parentCompanyId || '',
          isInternal: true,
          isPublic: false,
          objective: '',
          metadata: {
            stakeholderType: '',
            department: '',
            region: ''
          }
        });
        
        // Store the created stakeholder for display
        setCreatedStakeholder(result.stakeholder);
        
        // Select the newly created stakeholder
        if (onSuccess) {
          onSuccess(result.stakeholder);
        }
      } else {
        setError(result.message || 'Failed to create stakeholder');
      }
    } catch (err) {
      logger.error('Error creating stakeholder:', err);
      setError('Failed to create stakeholder');
    } finally {
      setLoading(false);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setError('');
    setCreatedStakeholder(null);
    setStakeholderData({
      name: '',
      parentCompanyId: parentCompanyId || '',
      isInternal: true,
      isPublic: false,
      objective: '',
      metadata: {
        stakeholderType: '',
        department: '',
        region: ''
      }
    });
  };

  return (
    <>
      {createdStakeholder ? (
        // Show created stakeholder as a tile
        <div 
          className={`${styles.stakeholderItem} ${styles.selectedItem}`}
          onClick={() => onSuccess && onSuccess(createdStakeholder)}
        >
          <div className={styles.stakeholderName}>
            <strong>{createdStakeholder.name}</strong>
            {createdStakeholder.objective && (
              <span className={styles.stakeholderObjective}>
                {createdStakeholder.objective}
              </span>
            )}
            <span className={styles.stakeholderType}>
              {createdStakeholder.metadata?.stakeholderType || 'Stakeholder'}
            </span>
            <span className={styles.stakeholderCompany}>
              {userCompanies.find(c => c.id === createdStakeholder.parentCompanyId)?.name || 'Unknown Organization'}
            </span>
            <span className={`${styles.categoryBadge} ${createdStakeholder.isInternal ? styles.internalCategory : styles.externalCategory}`}>
              {createdStakeholder.isInternal ? 'Internal' : 'External'}
            </span>
          </div>
          <div className={styles.stakeholderActions}>
            <Button 
              variant="outline" 
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setShowModal(true);
              }}
            >
              Create Another
            </Button>
          </div>
        </div>
      ) : (
        // Show creation button
        <Button 
          variant="primary" 
          onClick={() => setShowModal(true)}
          className={styles.createStakeholderButton}
        >
          Create New Stakeholder
        </Button>
      )}

      <Modal show={showModal} onHide={handleCloseModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Create New Stakeholder</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && <div className={styles.errorMessage}>{error}</div>}
          
          <form onSubmit={handleCreateStakeholder}>
            <Row>
              <Col md={6}>
                <div className={styles.formGroup}>
                  <label>Stakeholder Name *</label>
                  <input
                    type="text"
                    value={stakeholderData.name}
                    onChange={(e) => setStakeholderData(prev => ({ ...prev, name: e.target.value }))}
                    className={styles.formInput}
                    required
                    placeholder="e.g., Customer Support Team, Engineering Department"
                  />
                </div>
              </Col>
              <Col md={6}>
                <div className={styles.formGroup}>
                  <label>Stakeholder Type</label>
                  <select
                    value={stakeholderData.metadata.stakeholderType}
                    onChange={(e) => setStakeholderData(prev => ({ 
                      ...prev, 
                      metadata: { ...prev.metadata, stakeholderType: e.target.value }
                    }))}
                    className={styles.formInput}
                  >
                    <option value="">Select type...</option>
                    <option value="department">Department</option>
                    <option value="team">Team</option>
                    <option value="employee">Employee Group</option>
                    <option value="customer">Customer Group</option>
                    <option value="partner">Partner</option>
                    <option value="supplier">Supplier</option>
                    <option value="investor">Investor</option>
                  </select>
                </div>
              </Col>
            </Row>
            
            <Row>
              <Col md={6}>
                <div className={styles.formGroup}>
                  <label>Organization *</label>
                  <select
                    value={stakeholderData.parentCompanyId}
                    onChange={(e) => setStakeholderData(prev => ({ ...prev, parentCompanyId: e.target.value }))}
                    className={styles.formInput}
                    required
                    disabled={companiesLoading}
                  >
                    {companiesLoading ? (
                      <option value="">Loading organizations...</option>
                    ) : userCompanies.length === 0 ? (
                      <option value="">No organizations available</option>
                    ) : (
                      <>
                        <option value="">Select an organization...</option>
                        {userCompanies.map(company => (
                          <option key={company.id} value={company.id}>
                            {company.name}
                          </option>
                        ))}
                      </>
                    )}
                  </select>
                </div>
              </Col>
            </Row>
            
            <Row>
              <Col md={6}>
                <div className={styles.formGroup}>
                  <label>Department</label>
                  <input
                    type="text"
                    value={stakeholderData.metadata.department}
                    onChange={(e) => setStakeholderData(prev => ({ 
                      ...prev, 
                      metadata: { ...prev.metadata, department: e.target.value }
                    }))}
                    className={styles.formInput}
                    placeholder="e.g., Engineering, Marketing, Sales"
                  />
                </div>
              </Col>
              <Col md={6}>
                <div className={styles.formGroup}>
                  <label>Region</label>
                  <input
                    type="text"
                    value={stakeholderData.metadata.region}
                    onChange={(e) => setStakeholderData(prev => ({ 
                      ...prev, 
                      metadata: { ...prev.metadata, region: e.target.value }
                    }))}
                    className={styles.formInput}
                    placeholder="e.g., North America, Europe, APAC"
                  />
                </div>
              </Col>
            </Row>

            <div className={styles.formGroup}>
              <label>Objective</label>
              <textarea
                value={stakeholderData.objective}
                onChange={(e) => setStakeholderData(prev => ({ ...prev, objective: e.target.value }))}
                className={styles.formTextarea}
                placeholder="What is this stakeholder group's main objective or purpose?"
                rows={3}
              />
            </div>

            <Row>
              <Col md={6}>
                <div className={styles.formGroup}>
                  <label className={styles.checkboxLabel}>
                    <input
                      type="checkbox"
                      checked={stakeholderData.isInternal}
                      onChange={(e) => setStakeholderData(prev => ({ ...prev, isInternal: e.target.checked }))}
                      className={styles.checkbox}
                    />
                    Internal Stakeholder
                  </label>
                  <small className={styles.helpText}>
                    Internal stakeholders are part of your organization (employees, departments)
                  </small>
                </div>
              </Col>
              <Col md={6}>
                <div className={styles.formGroup}>
                  <label className={styles.checkboxLabel}>
                    <input
                      type="checkbox"
                      checked={stakeholderData.isPublic}
                      onChange={(e) => setStakeholderData(prev => ({ ...prev, isPublic: e.target.checked }))}
                      className={styles.checkbox}
                    />
                    Publicly Discoverable
                  </label>
                  <small className={styles.helpText}>
                    Public stakeholders can be discovered by others in your organization
                  </small>
                </div>
              </Col>
            </Row>

            <div className={styles.modalActions}>
              <Button variant="primary" type="submit" disabled={loading}>
                {loading ? 'Creating...' : 'Create Stakeholder'}
              </Button>
              <Button 
                variant="outline-neutral" 
                type="button"
                onClick={handleCloseModal}
              >
                Cancel
              </Button>
            </div>
          </form>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default StakeholderCreationButton;
